import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import MascotPlaceholder from "@/components/MascotPlaceholder";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";
import { Suitcase, X, ArrowRight } from "@phosphor-icons/react";

// Countries known to the app. `active: true` = fully supported; others show a
// disabled pill with a "Presto disponibile" label, same treatment as future
// phases (Check-in, A bordo, Landing home).
const COUNTRIES = [
  { code: "US", flag: "🇺🇸", name: { it: "Stati Uniti", en: "United States" }, active: true },
  { code: "IE", flag: "🇮🇪", name: { it: "Irlanda", en: "Ireland" }, active: false },
  { code: "CA", flag: "🇨🇦", name: { it: "Canada", en: "Canada" }, active: false },
  { code: "AU", flag: "🇦🇺", name: { it: "Australia", en: "Australia" }, active: false },
  { code: "DE", flag: "🇩🇪", name: { it: "Germania", en: "Germany" }, active: false },
  { code: "AR", flag: "🇦🇷", name: { it: "Argentina", en: "Argentina" }, active: false },
];

export default function Confronto() {
  const { t, lang } = useApp();
  const navigate = useNavigate();
  const [country, setCountry] = useState("US");
  const [editorial, setEditorial] = useState({ themes: [], available: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/content/destination-editorial?country=${country}&lang=${lang}`)
      .then(r => setEditorial(r.data))
      .finally(() => setLoading(false));
  }, [country, lang]);

  const activeCountry = COUNTRIES.find(c => c.code === country);

  return (
    <div className="max-w-3xl mx-auto px-5 py-8 pb-32 sm:pb-28 space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-terracotta font-semibold font-sans">
            {t.phase.plan}
          </p>
          <h1 data-testid="confronto-title" className="font-display font-black text-3xl sm:text-4xl text-ink mt-1 tracking-tight">
            {t.modes.confronto.title}
          </h1>
          <p className="text-ink-soft mt-2 leading-relaxed max-w-xl">
            {t.modes.confronto.subtitle}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <MascotPlaceholder emotion="happy" src="/assets/boardy-energico.png" />
          <button
            data-testid="confronto-exit"
            aria-label={t.modes.explorativa.exit}
            onClick={() => navigate("/")}
            className="w-10 h-10 rounded-full border border-border bg-surface-card text-ink-soft hover:text-ink hover:bg-surface-alt transition-colors flex items-center justify-center"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
      </div>

      {/* Country selector (horizontal pills, mobile-scrollable) */}
      <div
        data-testid="country-selector"
        className="flex gap-2 overflow-x-auto hide-scrollbar -mx-5 px-5 pb-1"
      >
        {COUNTRIES.map(c => {
          const isActive = c.code === country && c.active;
          const isDisabled = !c.active;
          return (
            <button
              key={c.code}
              data-testid={`country-pill-${c.code}`}
              onClick={() => c.active && setCountry(c.code)}
              disabled={isDisabled}
              className={`shrink-0 flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-sans font-semibold transition-colors ${
                isActive
                  ? "bg-terracotta text-white border-terracotta"
                  : isDisabled
                  ? "bg-surface-alt/60 text-ink-soft/60 border-border cursor-not-allowed"
                  : "bg-surface-card text-ink border-border hover:border-terracotta/50"
              }`}
              aria-pressed={isActive}
            >
              <span className="text-base leading-none" aria-hidden>{c.flag}</span>
              <span>{c.name[lang] || c.name.it}</span>
              {isDisabled && (
                <span className="text-[10px] uppercase tracking-widest bg-ink/10 text-ink-soft px-2 py-0.5 rounded-full font-sans font-semibold">
                  {t.modes.confronto.soon}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Editorial content */}
      {loading ? (
        <div className="text-ink-soft font-sans text-sm">{t.common.loading}</div>
      ) : editorial.available ? (
        <div data-testid="editorial-list" className="space-y-4">
          {editorial.themes.map((theme, i) => (
            <motion.section
              key={theme.key}
              data-testid={`theme-${theme.key}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.24) }}
              className="rounded-3xl border border-border bg-surface-card p-6 sm:p-7 shadow-[0_4px_20px_rgba(61,64,91,0.04)]"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-8 h-8 rounded-full bg-terracotta/10 flex items-center justify-center text-terracotta font-display font-black text-sm">
                  {i + 1}
                </span>
                <h2 className="font-display font-extrabold text-xl sm:text-2xl text-ink tracking-tight">
                  {theme.title}
                </h2>
              </div>
              <p className="text-ink text-base leading-relaxed font-sans whitespace-pre-line">
                {theme.body}
              </p>
            </motion.section>
          ))}
        </div>
      ) : (
        <div data-testid="editorial-unavailable" className="rounded-3xl border border-dashed border-border bg-surface-alt/60 p-8 text-center">
          <div className="text-4xl mb-2" aria-hidden>{activeCountry?.flag}</div>
          <div className="font-display font-extrabold text-xl text-ink">
            {activeCountry?.name[lang] || activeCountry?.name.it} — {t.modes.confronto.soon}
          </div>
        </div>
      )}

      {editorial.available && (
        <div
          data-testid="confronto-continue-sticky-wrapper"
          className="fixed inset-x-0 bottom-0 z-40 pointer-events-none"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="bg-gradient-to-t from-surface via-surface/95 to-surface/0 pt-6 pb-3 sm:pb-4">
            <div className="max-w-3xl mx-auto px-5 pointer-events-auto">
              <Button
                data-testid="confronto-continue"
                onClick={() => trackEvent(
                  "Confronto Continue Clicked",
                  { country },
                  () => navigate("/organizza"),
                )}
                className="w-full rounded-full h-12 bg-terracotta hover:bg-terracotta-hover text-white font-sans font-bold shadow-[0_6px_24px_rgba(226,114,91,0.35)]"
              >
                <Suitcase size={18} weight="duotone" className="mr-2" />
                {t.modes.confronto.continueCta}
                <ArrowRight size={18} weight="bold" className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
